/**
 * Câblage de la synchronisation Hybride avec l'application réelle (licence signée, session, SQLite, API).
 * Aucun effet tant que la licence n'est pas « hybride » : Standalone et Cloud ne passent jamais ici.
 */
import type { AppContext } from '@application/context/AppContext';
import { onActiveTenantChanged } from '@application/licence/ActiveTenant';
import { LICENCE_UPDATED_EVENT } from '@application/licence/LicenceEntitlementService';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { getDeviceLabel } from '@infra/persistence/DeviceLabelStore';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import type { SqliteTableName } from '@infra/persistence/SqliteTableName';
import { createBusinessApiSyncPort } from '@infra/sync/hybrid/BusinessApiSyncPort';
import {
  createElectronSyncAdmin,
  createElectronSyncOutbox,
  isSyncBridgeAvailable,
} from '@infra/sync/hybrid/ElectronSyncOutbox';
import type { SyncNotice } from '@infra/sync/hybrid/HybridSyncEngine';
import { getAppVersion } from '@shared/constants/appVersion';
import { uuid } from '@shared/utils/uuid';
import type { HybridSyncInput } from './HybridSyncEligibility';
import { HybridSyncRuntime } from './HybridSyncRuntime';

let runtime: HybridSyncRuntime | null = null;
let noticeHandler: (notice: SyncNotice) => void = () => undefined;
let serverSessionHandler: (session: { user_id: string; role: string }) => void = () => undefined;

/** Le cache d'identité local suit le rôle renvoyé par le serveur pendant la session. */
export function setHybridSyncServerSessionHandler(handler: (session: { user_id: string; role: string }) => void): void {
  serverSessionHandler = handler;
}

/** L'interface branche ici ses notifications discrètes (toasts). */
export function setHybridSyncNoticeHandler(handler: (notice: SyncNotice) => void): void {
  noticeHandler = handler;
}

export function getHybridSyncRuntime(): HybridSyncRuntime {
  if (runtime) return runtime;
  runtime = new HybridSyncRuntime({
    createApi: () => createBusinessApiSyncPort(getBusinessApiClient()),
    createOutbox: (tenantId) => createElectronSyncOutbox(tenantId),
    createAdmin: (tenantId) => createElectronSyncAdmin(tenantId),
    appVersion: getAppVersion(),
    getDeviceLabel,
    putLocal: (table, row) => SqliteEntityBridge.put(table as SqliteTableName, row as { id: string }),
    newId: (prefix) => uuid(prefix),
    emit: (name, detail) => {
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }));
    },
    onNotice: (notice) => noticeHandler(notice),
    onServerSession: (session) => serverSessionHandler(session),
    listen: (event, handler) => {
      if (typeof window === 'undefined') return () => undefined;
      window.addEventListener(event, handler);
      return () => window.removeEventListener(event, handler);
    },
    withLock: async (tenantId, fn) => {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      if (!locks) return fn();
      return locks.request(`nexxus-sync-${tenantId}`, async () => fn());
    },
  });
  return runtime;
}

/** Test uniquement. */
export function resetHybridSyncRuntimeForTests(): void {
  runtime?.deactivate();
  runtime = null;
}

/** Lit l'état réel du poste (licence signée, session, SQLite, consentement) pour décider si la synchro tourne. */
export async function readHybridSyncInput(ctx: AppContext): Promise<HybridSyncInput> {
  const claims = ctx.getLicenceRuntime()?.getState()?.claims ?? null;
  const session = ctx.auth.getSession();
  const bridgeAvailable = isSyncBridgeAvailable();
  const sqliteIsPrimary = bridgeAvailable ? await SqliteEntityBridge.isSqlitePrimary().catch(() => false) : false;
  let localTenantId: string | null = null;
  let consentRecorded = false;
  const tenantId = session?.tenant_id ?? null;
  if (claims && bridgeAvailable && sqliteIsPrimary) {
    const row = await SqliteEntityBridge.get<{ id: string }>('tenants', claims.tenant_id).catch(() => null);
    localTenantId = row?.id ?? null;
    if (tenantId && window.electron?.syncGetState) {
      const local = await window.electron.syncGetState(tenantId).catch(() => null);
      consentRecorded = Boolean(local?.enabled && local.consentVersion);
    }
  }
  let sessionAccountVerified = false;
  if (session && bridgeAvailable) {
    const user = await ctx.localTables.get<{ id: string; identity_verified_at?: string }>('users', session.user_id).catch(() => null);
    sessionAccountVerified = Boolean(user?.identity_verified_at);
  }
  return {
    sessionUserId: session?.user_id ?? null,
    sessionAccountVerified,
    claims: claims
      ? {
          tenant_id: claims.tenant_id,
          ...(claims.data_mode ? { data_mode: claims.data_mode } : {}),
          ...(claims.deployment_mode ? { deployment_mode: claims.deployment_mode } : {}),
        }
      : null,
    sqliteIsPrimary,
    bridgeAvailable,
    sessionTenantId: tenantId,
    sessionRole: session?.role ?? null,
    localTenantId,
    consentRecorded,
  };
}

/**
 * Réévalue la synchronisation à chaque événement qui peut la changer : bascule de société, licence mise à jour.
 * Retourne la fonction de désabonnement.
 */
export function bindHybridSyncToApp(ctx: AppContext): () => void {
  const reevaluate = (): void => {
    void readHybridSyncInput(ctx)
      .then((input) => getHybridSyncRuntime().activate(input))
      .catch(() => undefined);
  };
  const stopSwitch = onActiveTenantChanged(reevaluate);
  window.addEventListener(LICENCE_UPDATED_EVENT, reevaluate);
  return () => {
    stopSwitch();
    window.removeEventListener(LICENCE_UPDATED_EVENT, reevaluate);
  };
}
